import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { NavCountsProvider } from "@/contexts/NavCountsContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Firm360 from "@/pages/Firm360";
import Admin from "@/pages/Admin";
import NewCustomers from "@/pages/NewCustomers";
import OldCustomers from "@/pages/OldCustomers";
import Renewals from "@/pages/Renewals";
import PowerUsers from "@/pages/PowerUsers";
import MIS from "@/pages/MIS";
import Activity from "@/pages/Activity";
import FollowUps from "@/pages/FollowUps";
import UsageMetrics from "@/pages/UsageMetrics";
import Enterprise from "@/pages/Enterprise";
import PendingTasks from "@/pages/PendingTasks";
import Reminders from "@/pages/Reminders";

function AppRouter() {
  const location = useLocation();
  // Detect OAuth return synchronously during render so the session_id is handled
  // BEFORE any ProtectedRoute tries to redirect to /login.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/firms/:subdomain"
        element={
          <ProtectedRoute>
            <Layout>
              <Firm360 />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/new-customers"
        element={
          <ProtectedRoute>
            <Layout>
              <NewCustomers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/old-customers"
        element={
          <ProtectedRoute>
            <Layout>
              <OldCustomers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/renewals"
        element={
          <ProtectedRoute>
            <Layout>
              <Renewals />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/power-users"
        element={
          <ProtectedRoute>
            <Layout>
              <PowerUsers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage-metrics"
        element={
          <ProtectedRoute>
            <Layout>
              <UsageMetrics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/follow-ups"
        element={
          <ProtectedRoute>
            <Layout>
              <FollowUps />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <Layout>
              <Activity />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Back-compat: old /call-logs deep links still work */}
      <Route path="/call-logs" element={<Navigate to="/activity" replace />} />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Layout>
              <PendingTasks />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <Layout>
              <Reminders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mis"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <MIS />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/enterprise"
        element={
          <ProtectedRoute>
            <Layout>
              <Enterprise />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Admin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NavCountsProvider>
          <ConfirmProvider>
            <AppRouter />
          </ConfirmProvider>
        </NavCountsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
